/**
 * Small themed primitives — heivol storage-lane tokens via Tailwind colour
 * aliases (see tailwind.config.js / index.css). No third-party UI lib keeps the
 * CSP `script-src 'self'` story clean (INV-11).
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';

type ButtonVariant = 'primary' | 'ghost' | 'danger';

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  const variants: Record<ButtonVariant, string> = {
    primary: 'bg-accent text-white hover:opacity-90',
    ghost: 'bg-surface-alt text-text hover:bg-border',
    danger: 'bg-error text-white hover:opacity-90',
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]} ${className}`}
      {...props}
    />
  );
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text outline-none placeholder:text-text-secondary focus-visible:shadow-focus ${className}`}
      {...props}
    />
  );
}

export function Textarea({
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={`w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text outline-none placeholder:text-text-secondary focus-visible:shadow-focus ${className}`}
      {...props}
    />
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-text">{label}</span>
      {children}
      {hint !== undefined && <span className="block text-xs text-text-secondary">{hint}</span>}
    </label>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-border bg-surface p-6 shadow-card ${className}`}>
      {children}
    </div>
  );
}

// Canonical Heivol Locker lock-state glyphs — KB canon:
// heivol-locker/brand/lock-locked.svg / lock-unlocked.svg (24×24, currentColor
// stroke, width 2, round caps/joins). Path geometry is verbatim from the canon;
// do not alter it. Emoji lock glyphs (🔒/🔓) are retired per the brand README.
// Sizing lives in `className` (kept off the svg width/height attrs so it stays
// crisp); colour inherits from the surrounding text via currentColor.
const LOCK_PATH_LOCKED =
  'M17 10V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V10M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z';
const LOCK_PATH_UNLOCKED =
  'M7 10V8C7 5.23858 9.23858 3 12 3C14.0503 3 15.8124 4.2341 16.584 6M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z';

export function LockGlyph({ unlocked, className }: { unlocked: boolean; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      <path
        d={unlocked ? LOCK_PATH_UNLOCKED : LOCK_PATH_LOCKED}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LockBadge({ unlocked }: { unlocked: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
        unlocked ? 'bg-accent-surface text-accent-text' : 'bg-surface-alt text-text-secondary'
      }`}
    >
      <LockGlyph unlocked={unlocked} className="h-3.5 w-3.5 shrink-0" />
      {unlocked ? 'Unlocked' : 'Locked'}
    </span>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-error/40 bg-error/10 px-3 py-2 text-sm text-error"
    >
      {children}
    </p>
  );
}

/**
 * Sibling of `ErrorNote` for outcomes that are not the user's fault and not retryable — a
 * capability the server has deliberately switched off, for instance. Same geometry as
 * `ErrorNote` so the two stack cleanly; warning lane rather than error lane because nothing has
 * gone wrong, and `role="status"` rather than `role="alert"` so it is announced without the
 * assertive interrupt an actual failure earns.
 *
 * The hue lives in the border and the tint, NOT the text: amber `--hv-warning` on the light
 * surface measures ~3:1, short of the 4.5:1 WCAG-AA floor for 14px body copy. Body text keeps
 * `--hv-text` (>10:1 in both themes) and the note still reads as a warning from its container.
 */
export function InfoNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-text"
    >
      {children}
    </p>
  );
}
